import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * 화면(라우트)이 자신의 재조회 함수를 헤더에 공개하는 통로.
 * RN 의 당겨서 새로고침 대신 브라우저에서는 헤더의 새로고침 버튼을 쓴다.
 */
export interface RefreshTarget {
  refresh: () => void;
  refreshing: boolean;
  updatedAt: number | null;
}

const RefreshContext = createContext<{
  target: RefreshTarget | null;
  publish: (target: RefreshTarget | null) => void;
}>({ target: null, publish: () => {} });

export function RefreshProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<RefreshTarget | null>(null);
  const publish = useCallback((next: RefreshTarget | null) => setTarget(next), []);
  const value = useMemo(() => ({ target, publish }), [target, publish]);

  return (
    <RefreshContext.Provider value={value}>{children}</RefreshContext.Provider>
  );
}

/** 현재 화면의 재조회 상태를 헤더에 공개한다. 언마운트 시 자동 해제. */
export function usePublishRefresh({
  refresh,
  refreshing,
  updatedAt,
}: RefreshTarget): void {
  const { publish } = useContext(RefreshContext);

  useEffect(() => {
    publish({ refresh, refreshing, updatedAt });
    return () => publish(null);
  }, [publish, refresh, refreshing, updatedAt]);
}

export function useRefreshTarget(): RefreshTarget | null {
  return useContext(RefreshContext).target;
}
